import React, { useEffect, useState } from 'react';
import {
  Mic,
  FileText,
  Pill,
  Stethoscope,
  CalendarDays,
  FolderHeart,
  Smartphone,
  ShieldCheck,
  ChevronDown,
  Sparkles,
  ClipboardList,
  PhoneCall,
} from 'lucide-react';
import logoWhite from 'url:../assets/logo-white.png';
import logo from 'url:../assets/logo.png';

const scrollToForm = () => {
  document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export function Header() {
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-navy-dark/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <img src={logoWhite} alt="پارسینا" className="h-9 w-9 object-contain" />
          <span className="text-lg font-black text-white">پارسینا</span>
        </div>
        <nav className="hidden items-center gap-6 text-sm text-white/80 sm:flex">
          <a href="#features" className="transition hover:text-white">قابلیت‌ها</a>
          <a href="#how" className="transition hover:text-white">شروع کار</a>
          <a href="#faq" className="transition hover:text-white">پرسش‌های پرتکرار</a>
        </nav>
        <button
          onClick={scrollToForm}
          className="rounded-lg bg-teal px-4 py-3 text-sm font-bold text-white transition hover:bg-teal/90 sm:py-2"
        >
          درخواست مشاوره
        </button>
      </div>
    </header>
  );
}

export function Hero() {
  return (
    <section className="bg-gradient-brand-dark text-white">
      <div className="mx-auto max-w-6xl px-4 pb-14 pt-10 text-center sm:pb-24 sm:pt-20">
        <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-medium sm:mb-6 sm:text-sm">
          <Sparkles size={16} aria-hidden="true" />
          ویژهٔ روز پزشک
        </span>
        {/* 25px on a 390px screen: at text-3xl the second line overflows a
            narrow phone and breaks mid-phrase. */}
        <h1 className="mx-auto max-w-3xl text-[25px] font-black leading-[1.5] sm:text-5xl sm:leading-[1.35]">
          روز پزشک مبارک؛
          <br />
          وقت‌تان را به طبابت برگردانید
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-sm leading-8 text-white/85 sm:mt-6 sm:text-lg sm:leading-9">
          پارسینا دستیار هوشمند مطب شماست: پروندهٔ الکترونیک بیمار، نوبت‌دهی شمسی، رونویسی
          زندهٔ ویزیت و نسخه‌نویسی با کمک هوش مصنوعی — همه فارسی، همه در یک پنل، ساخته‌شده
          برای مطب‌ها و کلینیک‌های ایرانی.
        </p>
        <div
          id="hero-cta"
          className="mt-8 flex flex-col items-center justify-center gap-3 sm:mt-9 sm:flex-row"
        >
          <button
            onClick={scrollToForm}
            className="w-full rounded-lg bg-white px-8 py-3.5 text-base font-black text-navy shadow-lift transition hover:bg-white/90 sm:w-auto"
          >
            درخواست مشاوره و نمایش پنل
          </button>
          <a
            href="#features"
            className="w-full rounded-lg border border-white/30 px-8 py-3.5 text-base font-bold text-white transition hover:bg-white/10 sm:w-auto"
          >
            آشنایی با قابلیت‌ها
          </a>
        </div>
        <p className="mt-6 text-xs text-white/60">
          مشاوره و معرفی پنل رایگان است؛ بدون نیاز به نصب — پارسینا کاملاً تحت وب است.
        </p>
      </div>
    </section>
  );
}

const PAINS = [
  {
    icon: FileText,
    title: 'مستندسازی، وقتِ ویزیت را می‌خورد',
    body: 'شرح‌حال، نسخه و خلاصهٔ ویزیت را پارسینا هم‌زمان با صحبت شما می‌نویسد؛ شما فقط تأیید می‌کنید.',
  },
  {
    icon: CalendarDays,
    title: 'نوبت‌ها و صف، سرِ خود می‌چرخند',
    body: 'نوبت‌دهی شمسی، صف انتظار و حضور پزشک در یک تقویم؛ منشی و بیمار هر دو وضعیت را می‌بینند.',
  },
  {
    icon: FolderHeart,
    title: 'سوابق بیمار پخش‌وپلاست',
    body: 'آزمایش، تصویربرداری، نسخه‌ها و تشخیص‌های قبلی، یک‌جا در پروندهٔ الکترونیک هر بیمار.',
  },
];

export function PainPoints() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-12 sm:py-20">
      <h2 className="text-center text-xl font-black text-ink sm:text-3xl">
        چرا پزشکان سراغ پارسینا می‌آیند؟
      </h2>
      <div className="mt-7 grid gap-3.5 sm:mt-10 sm:gap-5 sm:grid-cols-3">
        {PAINS.map(({ icon: Icon, title, body }) => (
          // Phone: icon sits beside the text so three cards don't cost three
          // screens. Tablet up: the original stacked card.
          <div
            key={title}
            className="flex gap-3 rounded-lg border border-line bg-card p-4 shadow-card sm:block sm:p-6"
          >
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy-50 text-navy sm:mb-4">
              <Icon size={22} aria-hidden="true" />
            </span>
            <div>
              <h3 className="mb-1 text-sm font-bold text-ink sm:mb-2 sm:text-base">{title}</h3>
              <p className="text-[12.5px] leading-[1.9] text-ink-soft sm:text-sm sm:leading-7">
                {body}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const FEATURES = [
  {
    icon: Mic,
    title: 'رونویسی زندهٔ ویزیت',
    body: 'صحبت‌های ویزیت به متن فارسی تبدیل می‌شود و خلاصهٔ ساخت‌یافتهٔ ویزیت آماده می‌شود.',
  },
  {
    icon: Sparkles,
    title: 'ثبت بیمار با گفتار',
    body: 'اطلاعات بیمار جدید را بگویید؛ فرم ثبت‌نام خودکار و فارسی پر می‌شود.',
  },
  {
    icon: Pill,
    title: 'نسخه‌نویسی هوشمند',
    body: 'جست‌وجو در فهرست داروهای ایران، کنترل تداخل دارویی و پیشنهادهای مبتنی بر تشخیص.',
  },
  {
    icon: Stethoscope,
    title: 'کمک‌تشخیص بالینی',
    body: 'تشخیص افتراقی پیشنهادی با میزان اطمینان، بر پایهٔ علائم و سوابق همان بیمار.',
  },
  {
    icon: CalendarDays,
    title: 'نوبت‌دهی و صف شمسی',
    body: 'تقویم جلالی، مدیریت صف مراجعان حضوری و نوبت‌های آنلاین بیماران.',
  },
  {
    icon: FolderHeart,
    title: 'پروندهٔ کامل بیمار',
    body: 'آزمایش‌ها، تصویربرداری (نمایش DICOM در مرورگر)، آلرژی‌ها و علائم حیاتی در یک‌جا.',
  },
  {
    icon: Smartphone,
    title: 'اپلیکیشن بیمار',
    body: 'بیماران‌تان نتایج، نسخه‌ها و یادآورها را می‌بینند و آنلاین نوبت می‌گیرند.',
  },
  {
    icon: ShieldCheck,
    title: 'ورود امن و استاندارد',
    body: 'احراز هویت متمرکز، سطوح دسترسی مجزا برای پزشک و منشی، و خروجی استاندارد FHIR.',
  },
];

export function Features() {
  return (
    <section id="features" className="bg-white py-12 sm:py-20">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-center text-xl font-black text-ink sm:text-3xl">
          یک دستیار، همهٔ کارهای مطب
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-[12.5px] leading-[1.9] text-ink-soft sm:text-sm sm:leading-7">
          پارسینا از ثبت بیمار تا پایان ویزیت کنار شماست — و همهٔ ابزارها فارسی و راست‌به‌چپ
          طراحی شده‌اند.
        </p>
        {/* Eight one-line features stack into eight screens at one column;
            paired up they stay scannable in a thumb-flick. */}
        <div className="mt-7 grid grid-cols-2 gap-2.5 sm:mt-10 sm:gap-5 lg:grid-cols-4">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-lg border border-line bg-card p-3.5 sm:p-5">
              <span className="mb-2.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-bg text-teal sm:mb-3 sm:h-10 sm:w-10">
                <Icon size={20} aria-hidden="true" className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
              </span>
              <h3 className="mb-1.5 text-[13px] font-bold text-ink sm:text-sm">{title}</h3>
              <p className="text-xs leading-6 text-ink-soft">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  {
    icon: ClipboardList,
    title: 'درخواست‌تان را ثبت کنید',
    body: 'همین پایین، نام و شماره‌تان را بگذارید؛ یک دقیقه هم طول نمی‌کشد.',
  },
  {
    icon: PhoneCall,
    title: 'کارشناس پارسینا تماس می‌گیرد',
    body: 'پنل را از نزدیک می‌بینید و متناسب با مطب‌تان راه‌اندازی می‌شود.',
  },
  {
    icon: Stethoscope,
    title: 'ویزیت با دستیار هوشمند',
    body: 'از اولین روز، مستندسازی و نوبت‌دهی را به پارسینا بسپارید.',
  },
];

export function HowItWorks() {
  return (
    <section id="how" className="mx-auto max-w-6xl px-4 py-12 sm:py-20">
      <h2 className="text-center text-xl font-black text-ink sm:text-3xl">
        شروع، ساده‌تر از چیزی است که فکر می‌کنید
      </h2>

      {/* Phone: a vertical timeline — three numbered cards read as three
          unrelated blocks on a narrow screen, a connected rail reads as one
          sequence. Tablet up keeps the original three cards. */}
      <ol className="mt-8 sm:hidden">
        {STEPS.map(({ title, body }, i) => (
          <li key={title} className="flex gap-3.5">
            <div className="flex flex-col items-center">
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-gradient-brand text-sm font-black text-white">
                {['۱', '۲', '۳'][i]}
              </span>
              {i < STEPS.length - 1 && <span className="my-1.5 w-0.5 grow bg-line" />}
            </div>
            <div className={i < STEPS.length - 1 ? 'pb-5' : ''}>
              <h3 className="mb-1 mt-1.5 text-sm font-bold text-ink">{title}</h3>
              <p className="text-[12.5px] leading-[1.9] text-ink-soft">{body}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-10 hidden gap-5 sm:grid sm:grid-cols-3">
        {STEPS.map(({ icon: Icon, title, body }, i) => (
          <div key={title} className="relative rounded-lg border border-line bg-card p-6 text-center shadow-card">
            <span className="absolute -top-3 right-6 rounded-full bg-gradient-brand px-3 py-0.5 text-xs font-black text-white">
              {['۱', '۲', '۳'][i]}
            </span>
            <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-navy-50 text-navy">
              <Icon size={24} aria-hidden="true" />
            </span>
            <h3 className="mb-2 font-bold text-ink">{title}</h3>
            <p className="text-sm leading-7 text-ink-soft">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const FAQS = [
  {
    q: 'پارسینا دقیقاً چیست؟',
    a: 'پارسینا یک پلتفرم هوشمند مدیریت مطب و کلینیک برای پزشکان ایرانی است: پروندهٔ الکترونیک بیمار، نوبت‌دهی، مستندسازی ویزیت با هوش مصنوعی، نسخه‌نویسی و اپلیکیشن بیمار — همه یک‌پارچه و فارسی.',
  },
  {
    q: 'آیا نیاز به نصب نرم‌افزار دارد؟',
    a: 'خیر. پارسینا کاملاً تحت وب است و روی مرورگر رایانه و موبایل کار می‌کند؛ برای شروع فقط ورود امن با حساب کاربری لازم است.',
  },
  {
    q: 'اطلاعات بیماران من کجا نگهداری می‌شود؟',
    a: 'داده‌ها در زیرساخت ابری داخل ایران و با احراز هویت متمرکز و سطوح دسترسی مجزا نگهداری می‌شوند؛ دسترسی هر نقش (پزشک، منشی) فقط به همان چیزی است که لازم دارد.',
  },
  {
    q: 'هزینهٔ استفاده چقدر است؟',
    a: 'مشاوره و معرفی پنل رایگان است. جزئیات پلن‌ها و شرایط ویژهٔ کمپین روز پزشک را کارشناس پارسینا در همان تماس اول برای‌تان شفاف می‌کند.',
  },
  {
    q: 'تقویم و تاریخ‌ها شمسی هستند؟',
    a: 'بله. تمام تاریخ‌ها، نوبت‌ها و گزارش‌ها بر پایهٔ تقویم شمسی (جلالی) و به زبان فارسی نمایش داده می‌شوند.',
  },
];

export function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="bg-white py-12 sm:py-20">
      <div className="mx-auto max-w-3xl px-4">
        <h2 className="text-center text-xl font-black text-ink sm:text-3xl">پرسش‌های پرتکرار</h2>
        <div className="mt-7 space-y-2.5 sm:mt-8 sm:space-y-3">
          {FAQS.map(({ q, a }, i) => (
            <div key={q} className="overflow-hidden rounded-lg border border-line bg-card">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex min-h-[48px] w-full items-center justify-between gap-3 px-4 py-4 text-right sm:px-5"
                aria-expanded={open === i}
              >
                <span className="text-[13px] font-bold text-ink sm:text-sm">{q}</span>
                <ChevronDown
                  size={18}
                  className={`shrink-0 text-ink-faint transition-transform ${open === i ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
              {open === i && (
                <p className="px-4 pb-4 text-[12.5px] leading-8 text-ink-soft sm:px-5 sm:pb-5 sm:text-sm">
                  {a}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section id="final-cta" className="mx-auto max-w-6xl px-4 pb-12 sm:pb-20">
      <div className="rounded-lg bg-gradient-brand px-5 py-8 text-center text-white shadow-lift sm:px-6 sm:py-12">
        <h2 className="text-lg font-black leading-[1.6] sm:text-3xl">
          به مناسبت روز پزشک، پارسینا را امتحان کنید
        </h2>
        <p className="mx-auto mt-2.5 max-w-xl text-[12.5px] leading-[1.9] text-white/90 sm:mt-3 sm:text-sm sm:leading-7">
          درخواست‌تان را ثبت کنید تا کارشناسان پارسینا پنل پزشک را از نزدیک به شما نشان دهند.
        </p>
        <button
          onClick={scrollToForm}
          className="mt-5 w-full rounded-lg bg-white px-8 py-3.5 text-[15px] font-black text-navy transition hover:bg-white/90 sm:mt-6 sm:w-auto sm:text-base"
        >
          ثبت درخواست مشاوره رایگان
        </button>
      </div>
    </section>
  );
}

export function StickyCta() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    // A phone visitor is always several screens from the form, so the bar is
    // the way back to it — but it must never sit on top of a section that
    // already offers the same action. It shows only while none of the page's
    // own calls to action are on screen.
    const gates = ['hero-cta', 'lead-form', 'final-cta']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!gates.length) return undefined;

    const onScreen = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) onScreen.add(entry.target.id);
        else onScreen.delete(entry.target.id);
      });
      setVisible(onScreen.size === 0);
    });
    gates.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 border-t border-line bg-white/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-6px_24px_rgba(26,35,50,0.12)] backdrop-blur transition-transform duration-200 sm:hidden ${
        visible ? 'translate-y-0' : 'pointer-events-none translate-y-full'
      }`}
      aria-hidden={!visible}
    >
      <button
        onClick={scrollToForm}
        tabIndex={visible ? 0 : -1}
        className="w-full rounded-lg bg-gradient-brand px-6 py-3.5 text-[15px] font-black text-white"
      >
        ثبت درخواست مشاوره رایگان
      </button>
    </div>
  );
}

export function Footer() {
  return (
    // No sticky-bar clearance needed: FinalCta sits directly above and is on
    // screen whenever the footer is, so the bar is always stood down here.
    <footer className="bg-navy-dark py-10 text-center text-white/70">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-4 flex items-center justify-center gap-2">
          <img src={logoWhite} alt="" className="h-8 w-8 object-contain" />
          <span className="font-black text-white">پارسینا</span>
        </div>
        <p className="text-xs leading-6">
          دستیار هوشمند پزشکی برای مطب‌ها و کلینیک‌های ایرانی —{' '}
          <a href="https://parsinabot.ir" dir="ltr" className="text-teal hover:underline">
            parsinabot.ir
          </a>
        </p>
        {/* Latin run in an RTL line: without dir="ltr" the © jumps to the end. */}
        <p className="mt-2 text-[11px] text-white/40">
          <span dir="ltr">© {new Date().getFullYear()} Parsina</span>
        </p>
      </div>
    </footer>
  );
}

export { logo };
